import { useBusinessContext } from '@context'
import { IBusiness, updateBusiness } from '@query'
import { applyHttpFormErrors } from '@utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm, UseFormProps } from 'react-hook-form'

type Values = Partial<IBusiness>

interface Args {
  getDefaultValues: (data?: Partial<IBusiness>) => Values
  mode?: UseFormProps<Values>['mode']
  onSuccess?: (updated: IBusiness) => void
}

export const useInvoicingBasicsForm = ({ getDefaultValues, mode, onSuccess }: Args) => {
  const queryClient = useQueryClient()
  const { slug, data } = useBusinessContext()

  const { register, handleSubmit, setError, reset, setValue, watch, formState } = useForm<Values>({
    mode,
    defaultValues: getDefaultValues(data)
  })

  useEffect(() => {
    reset(getDefaultValues(data))
  }, [data, getDefaultValues, reset])

  const updateMutation = useMutation({
    mutationFn: (payload: Values) => updateBusiness({ slug }, payload),
    onSuccess: updated => {
      queryClient.setQueryData([slug], updated)
      void queryClient.invalidateQueries({ queryKey: ['businesses'] })
      void queryClient.invalidateQueries({ queryKey: [slug, 'features'] })
      onSuccess?.(updated)
    },
    onError: (error: unknown) => applyHttpFormErrors(error, setError)
  })

  return { register, handleSubmit, reset, setValue, watch, formState, updateMutation }
}
